import React, { useEffect, useMemo, useState } from 'react'

import { Link } from 'react-router-dom'

import StatusCard from '../components/status-card/StatusCard'
import Table from '../components/table/Table'
import Badge from '../components/badge/Badge'
import { fetchAllJobsForScope, fetchJobsByEmail, fetchProvizeZamestnanec, fetchRegularInspections, fetchUnpaidInvoices } from '../lib/crmClient'
import { getEmployeeIdByEmail } from '../lib/technicians'

const DEV_VIEW_ALL_EMAILS = new Set(['pavel.sedlacek@derator.cz'])
const TECHNICI = [
    { id: '1', fullName: 'Tomáš Raška', email: 'tomas.raska@derator.cz' },
    { id: '1007', fullName: 'Pavel Sedláček', email: 'pavel.sedlacek@derator.cz' },
    { id: '1011', fullName: 'Filip Landovský', email: 'filip.landovsky@derator.cz' },
    { id: '1012', fullName: 'Libor Landovský', email: 'libor.landovsky@derator.cz' },
    { id: '1013', fullName: 'Jaromír Mareš', email: 'jaromir.mares@derator.cz' },
    { id: '1027', fullName: 'Petr Sedláček', email: 'petr.sedlacek@derator.cz' },
    { id: '1042', fullName: 'Radek Steppan', email: 'radek.steppan@derator.cz' },
    { id: '1044', fullName: 'Adam Duda', email: 'adam.duda@derator.cz' },
    { id: '1048', fullName: 'Vladimír Nedopil', email: 'vladimir.nedopil@derator.cz' },
    { id: '1050', fullName: 'Lukáš Franěk', email: 'lukas.franek@derator.cz' },
]

const normalizeEmail = (value) => String(value || '').trim().toLowerCase()

const formatDateCz = (value) => {
    if (!value) return '-'
    const raw = String(value).slice(0, 10)
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!match) return String(value)
    const [, year, month, day] = match
    return `${day}.${month}.${year}`
}

const matchesCurrentUserInspection = (item, userEmail) => {
    const user = normalizeEmail(userEmail)
    if (!user) return false

    const itemEmail = normalizeEmail(item && item.ZamestnanecEmail)
    if (itemEmail && itemEmail === user) return true

    const itemEmployeeId = String(item && item.ZamestnanecId ? item.ZamestnanecId : '').trim()
    const userEmployeeId = String(getEmployeeIdByEmail(userEmail) || '').trim()
    return Boolean(itemEmployeeId && userEmployeeId && itemEmployeeId === userEmployeeId)
}

const statusToBadge = {
    'otevrena': 'primary',
    'naplanovana': 'warning',
    'v-reseni': 'warning',
    'dokoncena': 'success'
}

const inspectionStatusToBadge = (value) => {
    const text = String(value || '').trim().toLowerCase()
    if (text.includes('zpo')) return 'danger'
    if (text.includes('napl')) return 'success'
    return 'warning'
}

const toMonthKey = (value) => {
    if (!value) return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

const formatCzkInteger = (value) => {
    const n = Number(value)
    const rounded = Number.isFinite(n) ? Math.round(n) : 0
    return `${rounded.toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} Kč`
}

const formatMonthYearCz = (date) => {
    return new Intl.DateTimeFormat('cs-CZ', { month: 'long', year: 'numeric' }).format(date)
}

const previousMonthPeriod = () => {
    const now = new Date()
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    return { year: d.getFullYear(), month: d.getMonth() + 1 }
}

const invoiceOverdueDays = (value) => {
    const due = new Date(value)
    if (Number.isNaN(due.getTime())) return 0
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    due.setHours(0, 0, 0, 0)
    return Math.max(0, Math.floor((today.getTime() - due.getTime()) / 86400000))
}

const Dashboard = ({ email }) => {
    const normalizedEmail = normalizeEmail(email)
    const canViewAll = DEV_VIEW_ALL_EMAILS.has(normalizedEmail)
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 900)

    const [jobs, setJobs] = useState([])
    const [inspections, setInspections] = useState([])
    const [lastMonthProvize, setLastMonthProvize] = useState(null)
    const [overdueInvoices, setOverdueInvoices] = useState([])
    const [overdueLoading, setOverdueLoading] = useState(false)
    const [loading, setLoading] = useState(false)
    const [inspectionsLoading, setInspectionsLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 900)
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [])

    useEffect(() => {
        let cancelled = false

        const run = async () => {
            if (!cancelled) {
                setLoading(true)
                setError('')
            }
            try {
                const wantsAll = canViewAll
                const list = wantsAll
                    ? await fetchAllJobsForScope({
                        emails: TECHNICI.map((item) => item.email),
                        currentUserEmail: email,
                    })
                    : await fetchJobsByEmail(email)
                if (!cancelled) {
                    setJobs(list)
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e instanceof Error ? e.message : 'Načtení zakázek selhalo')
                }
            } finally {
                if (!cancelled) {
                    setLoading(false)
                }
            }
        }

        run()

        return () => {
            cancelled = true
        }
    }, [email, canViewAll])

    useEffect(() => {
        let cancelled = false

        const run = async () => {
            try {
                const employeeId = getEmployeeIdByEmail(email)
                if (!employeeId) {
                    if (!cancelled) setLastMonthProvize(0)
                    return
                }
                const period = previousMonthPeriod()
                const rows = await fetchProvizeZamestnanec({
                    zamestnanecId: employeeId,
                    year: period.year,
                    month: period.month,
                    detail: 0,
                    email,
                })
                const total = rows.reduce((sum, item) => sum + (Number(item.Provize_ZAM) || 0), 0)
                if (!cancelled) {
                    setLastMonthProvize(Math.ceil(total))
                }
            } catch {
                if (!cancelled) {
                    setLastMonthProvize(null)
                }
            }
        }

        run()

        return () => {
            cancelled = true
        }
    }, [email])

    useEffect(() => {
        let cancelled = false

        const run = async () => {
            if (!cancelled) setOverdueLoading(true)
            try {
                const result = await fetchUnpaidInvoices({ includeOld: true })
                if (!cancelled) setOverdueInvoices(result.items)
            } catch {
                if (!cancelled) setOverdueInvoices([])
            } finally {
                if (!cancelled) setOverdueLoading(false)
            }
        }

        run()
        return () => { cancelled = true }
    }, [])

    useEffect(() => {
        let cancelled = false
        const controller = new AbortController()

        const run = async () => {
            if (!cancelled) {
                setInspectionsLoading(true)
            }
            try {
                const list = await fetchRegularInspections({ email, signal: controller.signal })
                if (!cancelled) {
                    setInspections(list)
                }
            } catch {
                if (!cancelled) {
                    setInspections([])
                }
            } finally {
                if (!cancelled) {
                    setInspectionsLoading(false)
                }
            }
        }

        run()

        return () => {
            cancelled = true
            controller.abort()
        }
    }, [email])

    const stats = useMemo(() => {
        const now = new Date()
        const thisMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
        const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        const lastMonthKey = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, '0')}`

        const data = {
            thisMonthCount: 0,
            lastMonthCount: 0,
            thisMonthKm: 0,
            thisMonthAmount: 0,
        }

        jobs.forEach((item) => {
            const monthKey = toMonthKey(item.Datum || item.DatumNaplanovano)
            if (!monthKey) return

            if (monthKey === thisMonthKey) {
                data.thisMonthCount += 1
                data.thisMonthKm += Math.round(Number(item.Doprava) || 0)
                data.thisMonthAmount += Math.round(Number(item.CenaZakazky) || 0)
            }

            if (monthKey === lastMonthKey) {
                data.lastMonthCount += 1
            }
        })

        return data
    }, [jobs])

    const overdueStats = useMemo(() => {
        const rows = overdueInvoices.map((item) => ({
            amount: Number(item.total) || 0,
            days: invoiceOverdueDays(item.date_due),
        })).filter((item) => item.days > 30)
        return {
            count: rows.length,
            amount: rows.reduce((sum, item) => sum + item.amount, 0),
        }
    }, [overdueInvoices])

    const monthLabels = useMemo(() => {
        const now = new Date()
        const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        return {
            thisMonth: formatMonthYearCz(now),
            lastMonth: formatMonthYearCz(lastMonthDate),
        }
    }, [])

    const latest = useMemo(() => {
        return [...jobs]
            .sort((a, b) => (b.ZakazkaId || 0) - (a.ZakazkaId || 0))
            .slice(0, 8)
    }, [jobs])

    const upcomingInspections = useMemo(() => {
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        return inspections
            .filter((item) => {
                if (!canViewAll && !matchesCurrentUserInspection(item, email)) return false
                if (!item.NovePlanovanaKontrolaDatum) return false
                const date = new Date(item.NovePlanovanaKontrolaDatum)
                if (Number.isNaN(date.getTime())) return false
                return date >= today
            })
            .sort((a, b) => new Date(a.NovePlanovanaKontrolaDatum).getTime() - new Date(b.NovePlanovanaKontrolaDatum).getTime())
            .slice(0, 8)
    }, [inspections, canViewAll, email])

    return (
        <div>
            <h2 className="page-header">Dashboard technika</h2>

            {isMobile ? (
                <Link to="/scan-protokoly" className="card dashboard-scan-cta">
                    <div className="dashboard-scan-cta__content">
                        <span className="dashboard-scan-cta__eyebrow">Rychlá akce</span>
                        <h3>Skenovat protokol</h3>
                        <p>Pořídit nebo vybrat fotografie protokolu.</p>
                    </div>
                    <span className="btn btn-primary dashboard-scan-cta__button">
                        <i className="bx bx-scan" aria-hidden="true" />
                        Začít skenovat
                    </span>
                </Link>
            ) : null}

            {error ? <div className="card"><p className="crm-error">{error}</p></div> : null}

            <div className="row dashboard-stats-row">
                <div className="dashboard-stats-col col-md-6 col-sm-12">
                    <StatusCard icon='bx bx-briefcase-alt-2' count={loading ? '...' : stats.thisMonthCount} title={`Zakázky ${monthLabels.thisMonth}`} countClassName='status-card__count--compact' />
                </div>
                <div className="dashboard-stats-col col-md-6 col-sm-12">
                    <StatusCard icon='bx bx-history' count={loading ? '...' : stats.lastMonthCount} title={`Zakázky ${monthLabels.lastMonth}`} countClassName='status-card__count--compact' />
                </div>
                <div className="dashboard-stats-col col-md-6 col-sm-12">
                    <StatusCard
                        icon='bx bx-map'
                        count={loading ? '...' : `${stats.thisMonthKm.toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} km`}
                        title='Najeto tento měsíc'
                        countClassName='status-card__count--compact'
                    />
                </div>
                <div className="dashboard-stats-col col-md-6 col-sm-12">
                    <StatusCard
                        icon='bx bx-wallet'
                        count={loading ? '...' : formatCzkInteger(stats.thisMonthAmount)}
                        title='Měsíční obrat'
                        countClassName='status-card__count--compact'
                    />
                </div>
                <div className="dashboard-stats-col col-md-6 col-sm-12">
                    <StatusCard
                        icon='bx bx-money'
                        count={lastMonthProvize === null ? '...' : `${lastMonthProvize.toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} Kč`}
                        title='Provize minulý měsíc'
                        countClassName='status-card__count--compact'
                    />
                </div>
                <div className="dashboard-stats-col col-md-6 col-sm-12">
                    <Link to="/nezaplacene-faktury?all=1">
                        <StatusCard
                            icon='bx bx-receipt'
                            count={overdueLoading ? '...' : formatCzkInteger(overdueStats.amount)}
                            title={`Po splatnosti více než 30 dní (${overdueStats.count})`}
                            countClassName='status-card__count--compact'
                        />
                    </Link>
                </div>
            </div>

            {!isMobile ? (
            <div className="row">
                <div className={isMobile ? 'col-12' : 'col-7 col-md-12'}>
                    <div className="card">
                        <div className="card__header">
                            <h3>Nejnovější zakázky</h3>
                        </div>
                        <div className="card__body">
                            <Table
                                limit='8'
                                headData={['Datum zakázky', 'Zakázka', 'Zákazník', 'Status']}
                                renderHead={(item, index) => <th key={index}>{item}</th>}
                                bodyData={latest}
                                renderBody={(item) => (
                                    <tr key={item.ZakazkaId}>
                                        <td>{formatDateCz(item.Datum || '-')}</td>
                                        <td>{item.NazevZakazky}</td>
                                        <td>{item.NazevZakaznika || 'Bez zákazníka'}</td>
                                        <td>
                                            <Badge
                                                type={statusToBadge[String(item.Status || '').toLowerCase()] || 'primary'}
                                                content={item.Status || 'neznámý'}
                                            />
                                        </td>
                                    </tr>
                                )}
                            />
                        </div>
                        <div className="card__footer">
                            <Link to='/zakazky'>Otevřít přehled zakázek</Link>
                        </div>
                    </div>
                </div>
                <div className={isMobile ? 'col-12' : 'col-5 col-md-12'}>
                    <div className="card">
                        <div className="card__header">
                            <h3>Plánované kontroly</h3>
                        </div>
                        <div className="card__body">
                            <Table
                                limit='8'
                                headData={['Datum', 'Zákazník', 'Status']}
                                renderHead={(item, index) => <th key={index}>{item}</th>}
                                bodyData={upcomingInspections}
                                renderBody={(item) => (
                                    <tr key={`${item.ZakaznikId || 'z'}-${item.NovePlanovanaKontrolaDatum || 'd'}`}>
                                        <td>{formatDateCz(item.NovePlanovanaKontrolaDatum || '-')}</td>
                                        <td>{item.ZakaznikNazev || '-'}</td>
                                        <td>
                                            <Badge
                                                type={inspectionStatusToBadge(item.StatusKontroly)}
                                                content={item.StatusKontroly || '-'}
                                            />
                                        </td>
                                    </tr>
                                )}
                            />
                            {inspectionsLoading ? <p style={{ marginTop: 10 }}>Načítám plánované kontroly...</p> : null}
                        </div>
                        <div className="card__footer">
                            <Link to='/pravidelne-kontroly'>Otevřít detail kontrol</Link>
                        </div>
                    </div>
                </div>
            </div>
            ) : null}
        </div>
    )
}

export default Dashboard
